import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

const EMPTY_CATEGORIES: CustomerCategory[] = [];

export interface CustomerCategory {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  sort_order: number;
  is_default: boolean;
  booking_rules: Record<string, unknown> | null;
  cancellation_rules: Record<string, unknown> | null;
  membership_config: Record<string, unknown> | null;
  daily_minutes_limit: number | null;
  daily_limit_fallback_category: string | null;
  overflow_percent: number;
  created_at: string;
}

type CustomerCategoryRow = Tables<"customer_categories">;

export function useCustomerCategories(orgId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["customer-categories", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_categories")
        .select("*")
        .eq("organization_id", orgId!)
        .order("sort_order");
      if (error) throw error;

      // Cleanup: ta bort PAAS-kategorier som inte ska finnas
      const paasRows = (data ?? EMPTY_CATEGORIES).filter((c: CustomerCategoryRow) =>
        c.name.toLowerCase().includes("paas")
      );
      if (paasRows.length > 0) {
        await supabase
          .from("customer_categories")
          .delete()
          .in("id", paasRows.map((r: CustomerCategoryRow) => r.id));
        return (data ?? EMPTY_CATEGORIES).filter((c: CustomerCategoryRow) =>
          !c.name.toLowerCase().includes("paas")
        ) as CustomerCategory[];
      }

      return (data ?? EMPTY_CATEGORIES) as CustomerCategory[];
    },
    enabled: !!orgId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["customer-categories"] });

  const addCategory = useMutation({
    mutationFn: async ({ name, orgId: oid }: { name: string; orgId: string }) => {
      const slug = name.trim().toLowerCase().replace(/[^a-zåäö0-9]/g, "_");
      const maxSort = (query.data ?? EMPTY_CATEGORIES).reduce((m, c) => Math.max(m, c.sort_order), -1);
      const { error } = await supabase.from("customer_categories").insert({
        organization_id: oid,
        name: name.trim(),
        slug,
        sort_order: maxSort + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Kategori tillagd"); },
    onError: (e: Error) => {
      if (e.message?.includes("duplicate")) toast.error("Kategorin finns redan");
      else toast.error("Kunde inte lägga till kategori");
    },
  });

  const renameCategory = useMutation({
    mutationFn: async ({ id, oldSlug, newName, hallId }: { id: string; oldSlug: string; newName: string; hallId?: string }) => {
      const newSlug = newName.trim().toLowerCase().replace(/[^a-zåäö0-9]/g, "_");
      const { error } = await supabase
        .from("customer_categories")
        .update({ name: newName.trim(), slug: newSlug })
        .eq("id", id);
      if (error) throw error;
      // Update pricing_rules that reference old slug
      if (hallId) {
        await supabase
          .from("pricing_rules")
          .update({ category: newSlug })
          .eq("category", oldSlug)
          .eq("hall_id", hallId);
      } else {
        await supabase
          .from("pricing_rules")
          .update({ category: newSlug })
          .eq("category", oldSlug);
      }
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin-pricing"] });
      toast.success("Kategori uppdaterad");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte byta namn"); },
  });

  const deleteCategory = useMutation({
    mutationFn: async ({ id, moveTo }: { id: string; moveTo?: string }) => {
      // 1. Move customers to new category (or null) to avoid FK violation
      const { error: moveErr } = await supabase
        .from("customers")
        .update({ category_id: moveTo ?? null } as any)
        .eq("category_id", id);
      if (moveErr) throw moveErr;

      // 2. Delete pricing_rules referencing this category's slug
      const cat = (query.data ?? EMPTY_CATEGORIES).find(c => c.id === id);
      if (cat) {
        await supabase.from("pricing_rules").delete().eq("category", cat.slug);
      }

      // 3. Delete activity_category_prices referencing this category
      await supabase.from("activity_category_prices").delete().eq("customer_category_id", id);

      // 4. Now safe to delete the category itself
      const { error } = await supabase.from("customer_categories").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin-pricing"] });
      toast.success("Kategori borttagen");
    },
    onError: () => toast.error("Kunde inte ta bort kategori"),
  });

  const updateCategoryRules = useMutation({
    mutationFn: async ({ id, booking_rules, cancellation_rules, daily_minutes_limit, daily_limit_fallback_category, overflow_percent }: {
      id: string;
      booking_rules?: Record<string, unknown> | null;
      cancellation_rules?: Record<string, unknown> | null;
      daily_minutes_limit?: number | null;
      daily_limit_fallback_category?: string | null;
      overflow_percent?: number;
    }) => {
      const update: Partial<Pick<CustomerCategory, "booking_rules" | "cancellation_rules" | "daily_minutes_limit" | "daily_limit_fallback_category" | "overflow_percent">> = {};
      if (booking_rules !== undefined) update.booking_rules = booking_rules;
      if (cancellation_rules !== undefined) update.cancellation_rules = cancellation_rules;
      if (daily_minutes_limit !== undefined) update.daily_minutes_limit = daily_minutes_limit;
      if (daily_limit_fallback_category !== undefined) update.daily_limit_fallback_category = daily_limit_fallback_category;
      if (overflow_percent !== undefined) update.overflow_percent = overflow_percent;
      const { error } = await supabase.from("customer_categories").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Regler sparade"); },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte spara regler"); },
  });

  const updateMembershipConfig = useMutation({
    mutationFn: async ({ id, membership_config }: { id: string; membership_config: Record<string, unknown> | null }) => {
      const { error } = await supabase
        .from("customer_categories")
        .update({ membership_config })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Medlemskap uppdaterat"); },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte uppdatera medlemskap"); },
  });

  const getCategoryDependencies = async (categoryId: string, slug: string) => {
    const [customers, pricingRules, activityPrices] = await Promise.all([
      supabase.from("customers").select("id", { count: "exact", head: true }).eq("category_id", categoryId),
      supabase.from("pricing_rules").select("id", { count: "exact", head: true }).eq("category", slug),
      supabase.from("activity_category_prices").select("id", { count: "exact", head: true }).eq("customer_category_id", categoryId),
    ]);
    return {
      customerCount: customers.count ?? 0,
      pricingRuleCount: pricingRules.count ?? 0,
      activityPriceCount: activityPrices.count ?? 0,
    };
  };

  return {
    categories: query.data ?? [],
    isLoading: query.isLoading,
    addCategory,
    renameCategory,
    deleteCategory,
    updateCategoryRules,
    updateMembershipConfig,
    getCategoryDependencies,
  };
}
